import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Play } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-black tb-noise"
    >
      {/* LiDAR elevation background — breaklines/labels removed */}
      <motion.div
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.8, ease: [0.2, 0.8, 0.2, 1] }}
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/1lhknq9r_Screenshot%202026-06-05%20120401.jpg')",
          filter: "saturate(1.05) contrast(1.05)",
        }}
      />

      {/* Dark gradient wash — tuned for colorful elevation map legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.35)_70%,#000_100%)]" />
      <div className="absolute inset-0 tb-grid-overlay mix-blend-overlay opacity-50" />

      {/* Side vertical tickers */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3 text-[10px] tracking-[0.3em] text-zinc-400 font-mono">
        <span className="rotate-180" style={{ writingMode: "vertical-rl" }}>
          49.8951° N / 97.1384° W
        </span>
        <span className="h-16 w-px bg-white/20" />
      </div>
      <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3 text-[10px] tracking-[0.3em] text-zinc-400 font-mono">
        <span className="h-16 w-px bg-white/20" />
        <span style={{ writingMode: "vertical-rl" }}>WINNIPEG / MB / CA</span>
      </div>

      {/* Scanning line */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-0 right-0 tb-scan-line opacity-40" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-20 min-h-[100svh] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="font-display text-lg md:text-xl font-bold tracking-tight text-white"
        >
          Thunderbird Geospatial Inc.
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-4 inline-flex items-center gap-2 self-start rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] tracking-[0.2em] uppercase text-zinc-300 font-mono"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#3DC0AF] animate-pulse" />
          Aerial surveying • HQ Winnipeg, MB • Serving all of Canada
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-6 max-w-5xl text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]"
        >
          Precision mapping{" "}
          <span className="text-zinc-400 italic font-semibold">from</span>{" "}
          <br className="hidden sm:block" />
          <span className="bg-gradient-to-r from-white via-white to-[#3DC0AF] bg-clip-text text-transparent">
            above the prairies — and beyond.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45 }}
          className="mt-6 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
        >
          Survey-grade aerial data, LiDAR, orthomosaics, 3D terrain models, and
          GIS solutions for projects across Canada — guided by precision and
          grounded in respect for the Land and the communities connected to it.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="/#quote"
            data-testid="hero-cta-quote"
            className="group inline-flex items-center gap-3 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] text-white px-7 py-4 text-sm font-medium transition-colors"
          >
            Request a Survey
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a
            href="/#services"
            data-testid="hero-cta-services"
            className="group inline-flex items-center gap-3 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 text-white px-7 py-4 text-sm font-medium transition-colors"
          >
            <Play className="h-4 w-4" />
            Explore services
          </a>
          {/* Feb 2026 — Ground School CTA. Structurally matches the
              other two buttons in padding/height/gap. Teal accent border
              + pulsing dot for eye-catch; single label (no trailing
              arrow) so the button chrome stays symmetric. */}
          <a
            href="/groundschool"
            data-testid="hero-cta-groundschool"
            className="group inline-flex items-center gap-3 rounded-full border border-[#3DC0AF]/60 hover:border-[#3DC0AF] hover:bg-[#3DC0AF]/10 text-white px-7 py-4 text-sm font-medium transition-colors"
          >
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3DC0AF] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#3DC0AF]" />
            </span>
            <GraduationCap className="h-4 w-4 text-[#3DC0AF]" />
            TC Advanced Ground School — Enrol Now
          </a>
        </motion.div>

        {/* Trust caption */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.85 }}
          className="mt-16 md:mt-24 flex flex-wrap items-center justify-between gap-4 text-[11px] tracking-[0.3em] uppercase text-zinc-500 font-mono"
        >
          <span>Trusted by land owners, planners and builders</span>
          <span>Winnipeg HQ · Deploying across Canada</span>
        </motion.div>

        {/* Metric strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.95 }}
          className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-white/10 border-t border-white/10 pt-8"
        >
          {[
            { k: "DATA", v: "Survey-grade" },
            { k: "REACH", v: "Nationwide" },
            { k: "CERTIFIED", v: "Advanced RPAS" },
            { k: "PIPELINE", v: "End-to-end" },
          ].map((m) => (
            <div key={m.k} className="md:px-6">
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 font-mono">
                {m.k}
              </div>
              <div className="mt-2 text-xl md:text-2xl font-display font-bold">
                {m.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-black" />
    </section>
  );
};
